import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Nav, Table } from 'react-bootstrap';
import Select from 'react-select';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  LinhaCorrelacaoSolar,
  SolarService,
  TipoCorrelacaoSolar,
} from '@/modules/reports/solar.service';
import Loading from '@/shared/components/Carregando';
import Links from '@/shared/constants/navegacao';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { useNavigationView } from '@/shared/hooks/useNavigationView';

const solarService = new SolarService();

const TIPOS: { id: TipoCorrelacaoSolar; label: string }[] = [
  { id: 'defensoria', label: 'Defensoria' },
  { id: 'nucleo', label: 'Núcleo' },
  { id: 'comarca', label: 'Comarca' },
  { id: 'predio', label: 'Prédio' },
  { id: 'defensor', label: 'Defensor' },
  { id: 'servidor', label: 'Servidor' },
];

type Aba = 'vinculados' | 'nao-vinculados';
type Opcao = { value: string; label: string };

const menuSelect = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  menu: (base: Record<string, unknown>) => ({ ...base, minWidth: '28rem', width: 'max-content', maxWidth: '70vw' }),
};

export default function CorrelacaoSolarPage() {
  const { effectiveView, viewReady } = useNavigationView();

  if (!viewReady) return <Loading />;
  if (effectiveView !== NavigationView.Administrador) {
    return <Navigate to={Links.Nao_Autorizado} replace />;
  }

  return <CorrelacaoSolarConteudo />;
}

function CorrelacaoSolarConteudo() {
  const [tipo, setTipo] = useState<TipoCorrelacaoSolar>('defensoria');
  const [aba, setAba] = useState<Aba>('vinculados');
  const [linhas, setLinhas] = useState<LinhaCorrelacaoSolar[]>([]);
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(true);
  const [associando, setAssociando] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setEscolhas({});
    setBusca('');
    solarService.listarCorrelacao(tipo).then((resposta) => {
      if (!ativo) return;
      setLinhas(resposta.linhas ?? []);
      setCarregando(false);
    }).catch((error: unknown) => {
      if (!ativo) return;
      setLinhas([]);
      setCarregando(false);
      toast.error(error instanceof Error ? error.message : 'Falha ao listar a correlação.');
    });
    return () => {
      ativo = false;
    };
  }, [tipo, recarga]);

  const vinculados = useMemo(
    () => linhas.filter((linha) => linha.situacao === 'vinculado'),
    [linhas],
  );
  const semParSgi = useMemo(
    () => linhas.filter((linha) => linha.situacao === 'sem-par-sgi' && linha.sgiId),
    [linhas],
  );
  const semParSolar = useMemo(
    () => linhas.filter((linha) => linha.situacao === 'sem-par-solar' && linha.solarId != null),
    [linhas],
  );
  const termo = busca.trim().toLocaleLowerCase('pt-BR');
  const vinculadosVisiveis = useMemo(
    () => vinculados.filter((linha) => !termo || `${linha.sgiNome ?? ''} ${linha.solarNome ?? ''}`.toLocaleLowerCase('pt-BR').includes(termo)),
    [vinculados, termo],
  );
  const semParSgiVisiveis = useMemo(
    () => semParSgi.filter((linha) => !termo || (linha.sgiNome ?? '').toLocaleLowerCase('pt-BR').includes(termo)),
    [semParSgi, termo],
  );

  function opcoesDaLinha(sgiId: string) {
    const escolhida = escolhas[sgiId];
    const ocupados = new Set(
      Object.entries(escolhas)
        .filter(([id, solar]) => id !== sgiId && solar)
        .map(([, solar]) => solar),
    );
    return semParSolar.filter((linha) => {
      const id = String(linha.solarId);
      return id === escolhida || !ocupados.has(id);
    });
  }

  function escolher(sgiId: string, solarId: string) {
    setEscolhas((atual) => {
      const proximo = { ...atual, [sgiId]: solarId };
      if (!solarId) return proximo;
      for (const [outroId, outroSolar] of Object.entries(proximo)) {
        if (outroId !== sgiId && outroSolar === solarId) proximo[outroId] = '';
      }
      return proximo;
    });
  }

  async function associarDeNovo() {
    setAssociando(true);
    try {
      await solarService.associarCorrelacao();
      toast.success('Associação refeita. Só entrou par com nome único dos dois lados.');
      setRecarga((atual) => atual + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao associar os cadastros.');
    } finally {
      setAssociando(false);
    }
  }

  const paresEscolhidos = Object.entries(escolhas)
    .filter(([, solarId]) => solarId)
    .map(([sgiId, solarId]) => ({ sgiId, solarId: Number(solarId) }));

  async function vincularSelecionados() {
    if (paresEscolhidos.length === 0) return;
    setVinculando(true);
    try {
      const resposta = await solarService.vincularCorrelacao(tipo, paresEscolhidos);
      toast.success(`${resposta.vinculados} vínculo(s) gravado(s).`);
      setRecarga((atual) => atual + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao vincular os cadastros.');
    } finally {
      setVinculando(false);
    }
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h3 className="h5 mb-0">Correlação Solar</h3>
        <div className="d-flex align-items-center gap-2">
          <div style={{ minWidth: '12rem' }}>
            <Select<Opcao, false>
              inputId="correlacao-solar-tipo"
              classNamePrefix="react-select"
              aria-label="Cadastro"
              options={TIPOS.map((item) => ({ value: item.id, label: item.label }))}
              value={TIPOS.map((item) => ({ value: item.id, label: item.label })).find((item) => item.value === tipo)}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={menuSelect}
              onChange={(opcao) => {
                if (opcao) setTipo(opcao.value as TipoCorrelacaoSolar);
              }}
            />
          </div>
          <Button variant="outline-primary" size="sm" disabled={associando} onClick={() => void associarDeNovo()}>
            {associando ? 'Associando…' : 'Associar de novo'}
          </Button>
        </div>
      </div>
      <Form.Control
        className="mb-3"
        value={busca}
        placeholder="Digite para achar pelo nome"
        aria-label="Buscar pelo nome"
        onChange={(evento) => setBusca(evento.target.value)}
      />
      <Nav variant="tabs" className="mb-3" activeKey={aba} onSelect={(chave) => setAba((chave as Aba) ?? 'vinculados')}>
        <Nav.Item>
          <Nav.Link eventKey="vinculados">Vinculados ({vinculados.length})</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="nao-vinculados">
            Não vinculados ({semParSgi.length} no SGI, {semParSolar.length} no Solar)
          </Nav.Link>
        </Nav.Item>
      </Nav>
      {carregando ? (
        <Loading />
      ) : aba === 'vinculados' ? (
        <Table responsive striped hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Nome no SGI</th>
              <th>Nome no Solar</th>
              <th>Id Solar</th>
            </tr>
          </thead>
          <tbody>
            {vinculadosVisiveis.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-muted">Nenhum vínculo.</td>
              </tr>
            ) : vinculadosVisiveis.map((linha) => (
              <tr key={`v-${linha.sgiId}-${linha.solarId}`}>
                <td>{linha.sgiNome}</td>
                <td>{linha.solarNome ?? '—'}</td>
                <td>{linha.solarId}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <>
          <div className="d-flex justify-content-end mb-2">
            <Button
              size="sm"
              variant="success"
              disabled={paresEscolhidos.length === 0 || vinculando}
              onClick={() => void vincularSelecionados()}
            >
              {vinculando ? 'Vinculando…' : `Vincular (${paresEscolhidos.length})`}
            </Button>
          </div>
          <Table responsive striped hover size="sm" className="align-middle" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '72%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Nome no SGI</th>
              <th>Registro do Solar</th>
            </tr>
          </thead>
          <tbody>
            {semParSgiVisiveis.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-muted">
                  {semParSgi.length === 0 && semParSolar.length === 0
                    ? 'Nenhum cadastro sem vínculo.'
                    : semParSgi.length === 0
                      ? `${semParSolar.length} registro(s) do Solar ainda sem par no SGI.`
                      : 'Nenhum nome encontrado.'}
                </td>
              </tr>
            ) : semParSgiVisiveis.map((linha) => {
              const sgiId = linha.sgiId as string;
              const opcoes = opcoesDaLinha(sgiId).map((opcao) => ({
                value: String(opcao.solarId),
                label: `${opcao.solarNome} (${opcao.solarId})`,
              }));
              const escolhida = opcoes.find((opcao) => opcao.value === escolhas[sgiId]) ?? null;
              return (
                <tr key={`s-${sgiId}`}>
                  <td className="text-break">{linha.sgiNome}</td>
                  <td>
                    <Select<Opcao, false>
                      inputId={`correlacao-solar-${sgiId}`}
                      classNamePrefix="react-select"
                      aria-label={`Vincular ${linha.sgiNome}`}
                      options={opcoes}
                      value={escolhida}
                      placeholder="Digite o nome no Solar"
                      isClearable
                      isSearchable
                      noOptionsMessage={() => 'Nenhum registro livre'}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={menuSelect}
                      onChange={(opcao) => escolher(sgiId, opcao?.value ?? '')}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </>
      )}
    </div>
  );
}
